import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";

import {
  brand_orange,
  brand_orange_deep,
  brand_yellow,
  narrowScreenMediaQuery,
} from "../constants/styles";

// The "Lihat Semua …" affordance at the foot of a homepage carousel — the pill
// link into that section's full catalog, plus an optional hint underneath.
//
// Shared by WebinarCarousel and VideoPembelajaranCarousel. These three styled
// components were previously copy-pasted into both files and had already drifted
// (different transition timings, and only one of them had the hover box-shadow and
// the `:active` reset), which is exactly the bug duplication produces: a fix or a
// tweak lands on one button and not the other. The richer webinar version is the
// canonical one kept here.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 40px;

  @media ${narrowScreenMediaQuery} {
    margin-top: 28px;
  }
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 15px 34px;
  border-radius: 999px;
  border: 2px solid ${brand_orange_deep};
  background: #fff;
  color: ${brand_orange_deep};
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.01em;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(216, 89, 7, 0.15);
  transition:
    background 160ms ease,
    color 160ms ease,
    transform 120ms ease,
    box-shadow 160ms ease;

  & span[aria-hidden="true"] {
    transition: transform 160ms ease;
  }

  &:hover {
    background: ${brand_orange_deep};
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(216, 89, 7, 0.32);
  }
  &:hover span[aria-hidden="true"] {
    transform: translateX(4px);
  }
  &:active {
    transform: translateY(0);
  }
  &:focus-visible {
    outline: 3px solid ${brand_orange};
    outline-offset: 3px;
  }

  [data-theme="dark"] & {
    background: transparent;
    color: ${brand_yellow};
    border-color: ${brand_yellow};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  }
  [data-theme="dark"] &:hover {
    background: ${brand_yellow};
    color: #1f2937;
  }

  /* At 320px the longest label ("Lihat Semua Webinar dan Live Online Classes")
   * measured 473px against a 320px viewport: it spilled past both edges and forced
   * the whole document into horizontal scroll. Below the mobile gate the pill
   * becomes a full-width block and the label wraps.
   *
   * 'normal' rather than 'wrap' — identical effect here, but 'wrap' is only
   * understood by Chrome 114+ / Safari 17.4+ / Firefox 124+, and on anything older
   * the declaration is dropped so the nowrap above would still win. The bug would
   * survive on exactly the older mobile browsers this rule exists for.
   *
   * justify-content and text-align are required companions of width:100% —
   * inline-flex defaults to flex-start, so the label would otherwise hug the left
   * edge of a full-width pill. Horizontal padding comes down from 34px because it
   * was eating ~70px of a 320px screen.
   */
  @media ${narrowScreenMediaQuery} {
    box-sizing: border-box;
    width: 100%;
    white-space: normal;
    justify-content: center;
    text-align: center;
    padding: 14px 20px;
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  text-align: center;

  a {
    color: ${brand_orange_deep};
    font-weight: 700;
    text-decoration: underline;
  }

  [data-theme="dark"] & {
    color: #9ca3af;

    a {
      color: ${brand_yellow};
    }
  }
`;

function SeeAllSectionLink({ href, label, ariaLabel, hint = null }) {
  return (
    <Wrap>
      <Button href={href} aria-label={ariaLabel}>
        {label} <span aria-hidden="true">→</span>
      </Button>
      {hint && <Hint>{hint}</Hint>}
    </Wrap>
  );
}

export default React.memo(SeeAllSectionLink);
